import { useEffect, useMemo, useState } from "react";
import {
  ACTION_LABEL,
  LABELS,
  toastError,
  toastSuccess,
} from "../../../shared/constants";
import Navbar from "../../Navbar/NavbarComponent";
import Table from "../../../shared/components/Table";
import { useStoreSearchSiteCodeQuery } from "../../../Features/store/store-slice";
import SimpleButton from "../../../shared/components/Button/Button";
import ButtonLoader from "../../../shared/components/ButtonLoader";
import CheckBox from "../../../shared/components/CheckBox/CheckBox";
import {
  useSettingsGetQuery,
  useSettingsUpdateMutation,
  useAdminEditableFieldsGetQuery,
  useAdminEditableFieldsUpdateMutation,
} from "../../../Features/settings/settings-slice";
import Loader from "../../../shared/components/Loader";

const AVAILABLE_ORDER_FIELDS = [
  { key: "createdAt", label: "Order Date" },
  { key: "deliveryDate", label: "Store Delivery Date" },
  { key: "custDeliveryDate", label: "Customer Delivery Date" },
  { key: "firstName", label: "Customer Name" },
  { key: "destinationSiteCode", label: "Destination Site" },
  { key: "shortCode", label: "Short Code" },
  { key: "customerMobile", label: "Phone Number" },
  { key: "ordCode", label: "S.O No." },
  { key: "checkerId", label: "Checker Id" },
  { key: "address", label: "Customer Address" },
  { key: "createdBy", label: "Created By" },
  { key: "advanceAmount", label: "Advance Amount" },
  { key: "advanceRemarks", label: "Advance Remark" },
];

const Utility = (props: any) => {
  const {} = props;

  const [utilityList, setUtilityList]: any = useState([]);
  const [storeSelectIds, setStoreSelectIds] = useState<any[]>([]);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);

  const {
    data: searchSiteCodeData,
    isFetching: siteCodeFetching,
    isSuccess: searchSiteCodeSuccess,
  } = useStoreSearchSiteCodeQuery("", {
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true,
  });

  const {
    data: getAutoConfirmData,
    isFetching: getAutoConfirmFetching,
    isSuccess: getAutoConfirmSuccess,
  } = useSettingsGetQuery("", {
    refetchOnMountOrArgChange: true,
  });

  const [autoConfirmUpdate, { isLoading: autoConfirmUpdateLoading }] =
    useSettingsUpdateMutation();

  const {
    data: editableFieldsData,
    isFetching: editableFieldsFetching,
    isSuccess: editableFieldsSuccess,
    refetch: editableFieldsRefetch,
  } = useAdminEditableFieldsGetQuery("", {
    refetchOnMountOrArgChange: true,
  });

  const [adminEditableFieldsUpdate, { isLoading: fieldsUpdateLoading }] =
    useAdminEditableFieldsUpdateMutation();

  useEffect(() => {
    if (searchSiteCodeSuccess && getAutoConfirmSuccess) {
      const selectedStoreIds = getAutoConfirmData.item?.autoConfirm || [];
      const updatedData = searchSiteCodeData.list.map((item: any) => ({
        ...item,
        isStoreSelected: selectedStoreIds.includes(item._id),
      }));

      const selectedIds = updatedData
        .filter((item: any) => item.isStoreSelected)
        .map((item: any) => item._id);
      setUtilityList(updatedData);
      setStoreSelectIds(selectedIds);
    }
  }, [
    searchSiteCodeSuccess,
    getAutoConfirmSuccess,
    searchSiteCodeData,
    getAutoConfirmData,
  ]);

  useEffect(() => {
    if (editableFieldsSuccess && Array.isArray(editableFieldsData?.item)) {
      setSelectedFields(editableFieldsData.item);
    }
  }, [editableFieldsData, editableFieldsSuccess]);

  const storeCheck = (e: any, index: number) => {
    const updated = [...utilityList];
    updated[index] = {
      ...updated[index],
      isStoreSelected: e.target.checked,
    };

    setUtilityList(updated);

    const selected = updated
      .filter((item) => item.isStoreSelected)
      .map((item) => item._id);

    setStoreSelectIds(selected);
  };

  const allStoreCheck = (e: any, data: any[]) => {
    const updated = utilityList.map((item: any) => ({
      ...item,
      isStoreSelected: e.target.checked,
    }));

    setUtilityList(updated);

    const selected = e.target.checked
      ? updated.map((item: any) => item._id)
      : [];

    setStoreSelectIds(selected);
  };

  const handleSendToStore = async () => {
    const payload = {
      autoConfirm: storeSelectIds,
    };

    const autoConfirmResponse = await autoConfirmUpdate(payload)
      .unwrap()
      .catch((err: any) => {
        toastError(err.data?.errors[0]?.msg);
      });
    if (autoConfirmResponse && autoConfirmResponse?.status) {
      toastSuccess(autoConfirmResponse.message);
    }
  };

  const handleFieldToggle = (fieldKey: string) => {
    setSelectedFields((prev) => {
      if (prev.includes(fieldKey)) {
        return prev.filter(
          (k) =>
            k !== fieldKey &&
            k !== (fieldKey === "firstName" ? "customerName" : ""),
        );
      } else {
        const next = [...prev, fieldKey];
        if (fieldKey === "firstName" && !next.includes("customerName")) {
          next.push("customerName");
        }
        return next;
      }
    });
  };

  const handleSaveFields = async () => {
    const res = await adminEditableFieldsUpdate({ fields: selectedFields })
      .unwrap()
      .catch((err: any) => {
        toastError(err.data?.errors?.[0]?.msg || "Failed to update fields");
      });
    if (res && res.status) {
      toastSuccess(res.message);
      editableFieldsRefetch();
    }
  };

  const isAllFieldsSelected =
    AVAILABLE_ORDER_FIELDS.length > 0 &&
    AVAILABLE_ORDER_FIELDS.every((item) => selectedFields.includes(item.key));

  const handleSelectAllFields = () => {
    if (isAllFieldsSelected) {
      setSelectedFields([]);
    } else {
      const allKeys = AVAILABLE_ORDER_FIELDS.map((item) => item.key);
      if (!allKeys.includes("customerName")) {
        allKeys.push("customerName");
      }
      setSelectedFields(allKeys);
    }
  };

  const selectedFieldsCount = AVAILABLE_ORDER_FIELDS.filter((item) =>
    selectedFields.includes(item.key),
  ).length;

  const columns = useMemo(
    () => [
      {
        header: LABELS.NAME_TEXT,
        accessorKey: ACTION_LABEL.NAME,
        isShow: true,
        enableSorting: false,
      },
    ],
    [],
  );

  return (
    <>
      {siteCodeFetching && getAutoConfirmFetching && editableFieldsFetching ? (
        <Loader />
      ) : (
        <div className="full-height">
          <Navbar title={LABELS.UTILITY_TEXT} />
          <main className="main-content">
            <div className="pt-3">
              <div className="container-fluid">
                {/* Section 1: Store Exempted */}
                <div className="card custom-card mb-4">
                  <div className="card-header bg-white border-bottom d-flex flex-wrap justify-content-between align-items-center gap-2 py-3 px-4">
                    <div>
                      <h5 className="mb-0 text-black font-weight-600">
                        {LABELS.STORE_EXEMPTED_TEXT}
                      </h5>
                      <small className="text-muted">
                        Select stores that are exempted from sending orders to
                        Ginesys.
                      </small>
                    </div>
                    {utilityList.length > 0 && (
                      <div className="d-flex align-items-center gap-3">
                        <span className="badge bg-light text-dark font-weight-500 border px-3 py-2">
                          {storeSelectIds.length} / {utilityList.length} Stores
                          Exempted
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="card-body p-0">
                    <Table
                      data={utilityList}
                      columns={columns}
                      select={true}
                      selectMode="store"
                      storeCheck={storeCheck}
                      allStoreCheck={allStoreCheck}
                      allStoreCheckedCheckbox={
                        storeSelectIds.length === utilityList.length &&
                        utilityList.length > 0
                      }
                      coloumnDrop={false}
                      isFetching={siteCodeFetching}
                    />

                    <div className="row my-3 justify-content-center align-items-center">
                      <div className="col-12 text-center">
                        <SimpleButton
                          className="btn theme-btn theme-btn-sm me-2"
                          onClick={handleSendToStore}
                          text={
                            autoConfirmUpdateLoading ? (
                              <ButtonLoader />
                            ) : (
                              `Save Exempted Store/s`
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 2: Admin Order Editable Fields Configuration */}
                <div className="card custom-card mb-4">
                  <div className="card-header bg-white border-bottom d-flex flex-wrap justify-content-between align-items-center gap-2 py-3 px-4">
                    <div>
                      <h5 className="mb-0 text-black font-weight-600">
                        Admin Order Editable Fields Settings
                      </h5>
                      <small className="text-muted">
                        Select which fields can be manually edited by Admin on
                        the Order Detail page.
                      </small>
                    </div>
                    {AVAILABLE_ORDER_FIELDS.length > 0 && (
                      <div className="d-flex align-items-center gap-3">
                        <CheckBox
                          id="admin-field-select-all"
                          label="Select All"
                          value={isAllFieldsSelected}
                          onChange={handleSelectAllFields}
                          name="admin-field-select-all"
                        />
                        <span className="badge bg-light text-dark font-weight-500 border ms-2 px-3 py-2">
                          {selectedFieldsCount} /{" "}
                          {AVAILABLE_ORDER_FIELDS.length} Selected
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="card-body p-4">
                    <div className="row">
                      {AVAILABLE_ORDER_FIELDS.map((item) => (
                        <div key={item.key} className="col-md-3 col-sm-6 mb-3">
                          <CheckBox
                            id={`field-${item.key}`}
                            label={item.label}
                            value={selectedFields.includes(item.key)}
                            onChange={() => handleFieldToggle(item.key)}
                            name={`field-${item.key}`}
                          />
                        </div>
                      ))}
                    </div>
                    <div className="row mt-3">
                      <div className="col-12 text-end">
                        <SimpleButton
                          className="btn theme-btn theme-btn-sm"
                          onClick={handleSaveFields}
                          text={
                            fieldsUpdateLoading ? (
                              <ButtonLoader />
                            ) : (
                              `Save Order Field Settings`
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      )}
    </>
  );
};

export default Utility;
